import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCogs, faLayerGroup, faSyncAlt } from "@fortawesome/free-solid-svg-icons";
import { useBaseUrl } from "../../../utils/utils";
import ServiceCenterShell from "./ServiceCenterShell";

interface Category {
    id: number;
    name: string;
    description: string;
    subcategories_count: number;
    services_count: number;
}

interface PaginationMeta {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    hasNext: boolean;
    hasPrevious: boolean;
}

interface CategorySummary {
    total_categories: number;
    populated_categories: number;
    largest_category_name: string | null;
    largest_category_count: number;
    total_services: number;
}

const emptyPagination = (): PaginationMeta => ({ currentPage: 1, totalPages: 1, totalItems: 0, hasNext: false, hasPrevious: false });
const emptySummary = (): CategorySummary => ({ total_categories: 0, populated_categories: 0, largest_category_name: null, largest_category_count: 0, total_services: 0 });
const readPagination = (pagination: any, fallbackLength: number = 0): PaginationMeta => ({
    currentPage: Number(pagination?.current_page ?? pagination?.currentPage ?? 1),
    totalPages: Number(pagination?.total_pages ?? pagination?.totalPages ?? 1),
    totalItems: Number(pagination?.total_items ?? pagination?.totalItems ?? fallbackLength),
    hasNext: Boolean(pagination?.has_next ?? pagination?.hasNext ?? false),
    hasPrevious: Boolean(pagination?.has_previous ?? pagination?.hasPrevious ?? false),
});

const CategoriesPage: React.FC = () => {
    const baseUrl = useBaseUrl();
    const [categories, setCategories] = useState<Category[]>([]);
    const [summary, setSummary] = useState<CategorySummary>(emptySummary());
    const [pagination, setPagination] = useState<PaginationMeta>(emptyPagination());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);

    useEffect(() => {
        document.title = "Service Categories - Uzima Healthcare";
    }, []);

    useEffect(() => {
        FetchCategorySummaries(page);
    }, [baseUrl, page, search]);

    useEffect(() => {
        setPage(1);
    }, [search]);

    const FetchCategorySummaries = async (targetPage: number = 1) => {
        try {
            setLoading(true);
            setError(null);
            const params = new URLSearchParams({ page: String(targetPage), page_size: '12' });
            if (search) params.set('search', search);
            const response = await fetch(`${baseUrl}facilities/services/categories-summary/?${params.toString()}`);
            if (!response.ok) throw new Error("Failed to load categories");
            const payload = await response.json();
            const data = payload.data || {};
            const categoryList = Array.isArray(data.categories) ? data.categories : [];
            setCategories(categoryList);
            setSummary(data.summary || emptySummary());
            setPagination(readPagination(data.pagination, categoryList.length));
        } catch (err) {
            console.error("Error fetching categories:", err);
            setError("Unable to load service categories right now.");
            setCategories([]);
            setSummary(emptySummary());
            setPagination(emptyPagination());
        } finally {
            setLoading(false);
        }
    };

    const visibleCategories = useMemo(() => categories, [categories]);

    return (
        <ServiceCenterShell
            title="Service Categories"
            description="Browse the taxonomy powering your service catalogue and understand which categories are most populated."
            activeTab="categories"
            action={
                <button className="sc-btn sc-btn-outline" onClick={() => FetchCategorySummaries(page)}>
                    <FontAwesomeIcon icon={faSyncAlt} /> Refresh
                </button>
            }
        >
            <div className="sc-stats">
                <div className="sc-stat">
                    <div className="sc-stat-icon teal"><i className="bx bx-category"></i></div>
                    <div className="sc-stat-value">{summary.total_categories}</div>
                    <div className="sc-stat-label">Master Categories</div>
                </div>
                <div className="sc-stat">
                    <div className="sc-stat-icon blue"><i className="bx bx-layer-group"></i></div>
                    <div className="sc-stat-value">{summary.total_services}</div>
                    <div className="sc-stat-label">Services Catalogued</div>
                </div>
                <div className="sc-stat">
                    <div className="sc-stat-icon amber"><i className="bx bx-pie-chart-alt-2"></i></div>
                    <div className="sc-stat-value">{summary.populated_categories}</div>
                    <div className="sc-stat-label">Populated Categories</div>
                </div>
                <div className="sc-stat">
                    <div className="sc-stat-icon violet"><i className="bx bx-trending-up"></i></div>
                    <div className="sc-stat-value">{summary.largest_category_count}</div>
                    <div className="sc-stat-label">Largest Category Count</div>
                </div>
            </div>

            <div className="sc-toolbar">
                <div className="sc-search">
                    <i className="bx bx-search"></i>
                    <input placeholder="Search categories..." value={search} onChange={(event) => setSearch(event.target.value)} />
                </div>
                <span className="sc-chip soft">Categories sync from the central service directory</span>
            </div>

            {loading ? (
                <div className="sc-spinner"></div>
            ) : error ? (
                <div className="sc-empty">
                    <i className="bx bx-error-circle"></i>
                    <h3>Unable to load categories</h3>
                    <p>{error}</p>
                    <button className="sc-btn sc-btn-primary" onClick={() => FetchCategorySummaries(page)}><FontAwesomeIcon icon={faSyncAlt} /> Try again</button>
                </div>
            ) : visibleCategories.length === 0 ? (
                <div className="sc-empty">
                    <i className="bx bx-category"></i>
                    <h3>No categories found</h3>
                    <p>Try a different search term or refresh the directory.</p>
                </div>
            ) : (
                <>
                    <div className="sc-split">
                        <div className="sc-panel">
                            <div className="sc-panel-head">
                                <div>
                                    <h3>Category directory</h3>
                                    <p>All categories available for service organization.</p>
                                </div>
                            </div>
                            <div className="sc-panel-body">
                                <div className="sc-list-stack">
                                    {visibleCategories.map((category) => {
                                        const maxCount = summary.largest_category_count || 1;
                                        return (
                                            <div className="sc-list-item" key={category.id}>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                                                        <div className="sc-stat-icon blue" style={{ marginBottom: 0 }}>
                                                            <FontAwesomeIcon icon={faCogs} />
                                                        </div>
                                                        <div>
                                                            <strong>{category.name}</strong>
                                                            <small style={{ display: "block", marginTop: 3 }}>{category.description || "No description available"}</small>
                                                        </div>
                                                    </div>
                                                    <div className="sc-meter"><div style={{ width: `${Math.max(8, Math.round((category.services_count / maxCount) * 100))}%` }}></div></div>
                                                </div>
                                                <div style={{ textAlign: "right", minWidth: 140 }}>
                                                    <strong>{category.services_count}</strong>
                                                    <small style={{ display: "block", marginTop: 4 }}>services</small>
                                                    <small style={{ display: "block", marginTop: 4 }}>{category.subcategories_count} subcategories</small>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        <div className="sc-panel">
                            <div className="sc-panel-head">
                                <div>
                                    <h3>Category insights</h3>
                                    <p>Quick context about catalogue distribution.</p>
                                </div>
                            </div>
                            <div className="sc-panel-body">
                                <div className="sc-kpi-grid">
                                    <div className="sc-kpi-card">
                                        <h4>Most populated</h4>
                                        <strong>{summary.largest_category_name || "—"}</strong>
                                        <span>{summary.largest_category_count} linked services</span>
                                    </div>
                                    <div className="sc-kpi-card">
                                        <h4>Coverage rate</h4>
                                        <strong>{summary.total_categories ? `${Math.round((summary.populated_categories / summary.total_categories) * 100)}%` : "0%"}</strong>
                                        <span>categories currently in use</span>
                                    </div>
                                    <div className="sc-kpi-card">
                                        <h4>Average services</h4>
                                        <strong>{summary.populated_categories ? (summary.total_services / summary.populated_categories).toFixed(1) : "0.0"}</strong>
                                        <span>per populated category</span>
                                    </div>
                                    <div className="sc-kpi-card">
                                        <h4>Visible page size</h4>
                                        <strong>{visibleCategories.length}</strong>
                                        <span>categories on this page</span>
                                    </div>
                                </div>
                                <div className="sc-panel" style={{ marginTop: 18, marginBottom: 0 }}>
                                    <div className="sc-panel-body">
                                        <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                                            <div className="sc-stat-icon amber" style={{ marginBottom: 0 }}>
                                                <FontAwesomeIcon icon={faLayerGroup} />
                                            </div>
                                            <div>
                                                <h3 style={{ margin: 0, fontSize: "0.92rem" }}>How to manage pricing and services</h3>
                                                <p style={{ margin: "6px 0 0", fontSize: "0.78rem", color: "var(--uzima-shell-text-muted)" }}>
                                                    Categories come from the system-wide directory. Service activation and pricing still happen in My Services.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {pagination.totalPages > 1 && (
                        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, marginTop: 20 }}>
                            <button className="sc-btn sc-btn-ghost" disabled={!pagination.hasPrevious} onClick={() => setPage((prev) => prev - 1)}>Prev</button>
                            <span className="sc-chip soft">Page {pagination.currentPage} of {pagination.totalPages}</span>
                            <button className="sc-btn sc-btn-ghost" disabled={!pagination.hasNext} onClick={() => setPage((prev) => prev + 1)}>Next</button>
                        </div>
                    )}
                </>
            )}
        </ServiceCenterShell>
    );
};

export default CategoriesPage;
